import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Download, Pause, Play, RefreshCw, Trash2, X } from "lucide-react";
import { runs as runsApi } from "@/api/endpoints";
import { serverDetail } from "@/api/client";
import StatusPill from "@/components/StatusPill";
import SpinnerButton from "@/components/SpinnerButton";
import ConfirmDialog from "@/components/ConfirmDialog";
import YamlViewer from "@/components/YamlViewer";
import JsonViewer from "@/components/JsonViewer";
import KpiTile from "@/components/KpiTile";
import Card from "@/components/Card";
import EmptyState from "@/components/EmptyState";
import Trajectory3D from "@/components/Trajectory3D";
import SegmentedTabs from "@/components/SegmentedTabs";
import CopyButton from "@/components/CopyButton";
import { fmtBytes, fmtDuration, fmtNum, relativeTime } from "@/lib/format";
// Phase 4 — credibility badge + Phase-3 report export + reviewer flow.
import ConfidenceBadge from "@/components/ConfidenceBadge";
import ExportReportButton from "@/components/ExportReportButton";
import ReviewControls from "@/components/ReviewControls";
import LinkUptimeCard from "@/components/run/LinkUptimeCard";
import ParameterProvenanceSection from "@/components/ParameterProvenanceSection";

const TABS = ["Summary", "Trajectory", "Artifacts", "Log"];

function ImageThumb({ runId, name, onOpen }) {
    const [src, setSrc] = useState("");
    const [err, setErr] = useState("");
    useEffect(() => {
        let url = "";
        let cancelled = false;
        async function load() {
            try {
                const blob = await runsApi.artifactBlob(runId, name);
                if (cancelled) return;
                url = URL.createObjectURL(blob);
                setSrc(url);
            } catch (e) {
                if (!cancelled) setErr(serverDetail(e));
            }
        }
        load();
        return () => {
            cancelled = true;
            if (url) URL.revokeObjectURL(url);
        };
    }, [runId, name]);
    if (err) {
        return (
            <div className="text-[11px] text-ux-danger bg-ux-danger-soft border border-ux-danger/40 rounded-md p-2">
                {name}: {err}
            </div>
        );
    }
    if (!src) {
        return <div className="skeleton h-40 rounded-md" />;
    }
    return (
        <button
            type="button"
            className="block w-full bg-ux-bg-1 border border-ux-border-1 rounded-md overflow-hidden hover:border-ux-accent transition-colors"
            onClick={() => onOpen({ name, src })}
            data-testid={`run-detail-thumb-${name}`}
        >
            <img src={src} alt={name} className="w-full h-40 object-contain bg-ux-bg-0" />
            <div className="text-[11px] text-ux-text-2 px-2 py-1 truncate font-mono">{name}</div>
        </button>
    );
}

function CsvPreview({ runId, name }) {
    const [rows, setRows] = useState(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    async function load() {
        setLoading(true);
        setErr("");
        try {
            const txt = await runsApi.artifactText(runId, name);
            const lines = txt.split(/\r?\n/).filter((l) => l.length > 0).slice(0, 21);
            const header = lines.shift().split(",");
            const data = lines.map((l) => l.split(","));
            setRows({ header, data });
        } catch (e) {
            setErr(serverDetail(e));
        } finally {
            setLoading(false);
        }
    }

    return (
        <div>
            {!rows && (
                <SpinnerButton
                    variant="ghost"
                    loading={loading}
                    onClick={load}
                    testId={`run-detail-csv-preview-${name}`}
                    className="text-[11px]"
                >
                    Preview (first 20 rows)
                </SpinnerButton>
            )}
            {err && (
                <div className="text-[11px] text-ux-danger bg-ux-danger-soft border border-ux-danger/40 rounded-md p-2 mt-1">
                    {err}
                </div>
            )}
            {rows && (
                <div className="overflow-x-auto bg-ux-bg-1 border border-ux-border-1 rounded-md mt-2">
                    <table className="text-[11px] font-mono">
                        <thead className="text-ux-text-3 sticky top-0 bg-ux-bg-1">
                            <tr className="border-b border-ux-border-2">
                                {rows.header.map((h, i) => (
                                    <th key={i} className="px-2 py-1 text-left whitespace-nowrap">
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.data.map((r, i) => (
                                <tr key={i} className="border-t border-ux-border-1">
                                    {r.map((c, j) => (
                                        <td key={j} className="px-2 py-1 whitespace-nowrap">
                                            {c}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function JsonInline({ runId, name }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    async function load() {
        setLoading(true);
        setErr("");
        try {
            const j = await runsApi.artifactJson(runId, name);
            setData(j);
        } catch (e) {
            setErr(serverDetail(e));
        } finally {
            setLoading(false);
        }
    }
    return (
        <div>
            {!data && (
                <SpinnerButton
                    variant="ghost"
                    loading={loading}
                    onClick={load}
                    testId={`run-detail-json-preview-${name}`}
                    className="text-[11px]"
                >
                    Preview JSON
                </SpinnerButton>
            )}
            {err && (
                <div className="text-[11px] text-ux-danger bg-ux-danger-soft border border-ux-danger/40 rounded-md p-2 mt-1">
                    {err}
                </div>
            )}
            {data && <JsonViewer value={data} maxHeight="40vh" />}
        </div>
    );
}

export default function RunDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [run, setRun] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [tab, setTab] = useState("Summary");
    const [logData, setLogData] = useState(null);
    const [logLoading, setLogLoading] = useState(false);
    const [logErr, setLogErr] = useState("");
    const [logTail, setLogTail] = useState(500);
    const [logFollow, setLogFollow] = useState(true);
    const [confirmDel, setConfirmDel] = useState(false);
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [scenarioJson, setScenarioJson] = useState(null);
    const [scenarioErr, setScenarioErr] = useState("");
    const [lightbox, setLightbox] = useState(null);

    const fetchRun = useCallback(async () => {
        try {
            const data = await runsApi.get(id);
            setRun(data);
            setLoading(false);
        } catch (e) {
            setErr(serverDetail(e));
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchRun();
    }, [fetchRun]);

    const inflight = run?.status === "queued" || run?.status === "running" || run?.status === "cancelling";
    const cancellable = run?.status === "queued" || run?.status === "running";

    useEffect(() => {
        if (!inflight) return undefined;
        const t = setInterval(fetchRun, 3000);
        return () => clearInterval(t);
    }, [inflight, fetchRun]);

    const fetchLog = useCallback(async () => {
        setLogLoading(true);
        setLogErr("");
        try {
            const d = await runsApi.log(id, logTail);
            setLogData(d);
        } catch (e) {
            setLogErr(serverDetail(e));
        } finally {
            setLogLoading(false);
        }
    }, [id, logTail]);

    useEffect(() => {
        if (tab !== "Log") return undefined;
        fetchLog();
        if (!logFollow || !inflight) return undefined;
        const t = setInterval(fetchLog, 2000);
        return () => clearInterval(t);
    }, [tab, inflight, fetchLog, logFollow]);

    useEffect(() => {
        if (run?.status !== "succeeded") return;
        if (scenarioJson || scenarioErr) return;
        if (!run.artifacts?.some((a) => a.name === "scenario.json")) return;
        runsApi
            .artifactJson(id, "scenario.json")
            .then((j) => setScenarioJson(j))
            .catch((e) => setScenarioErr(serverDetail(e)));
    }, [run, id, scenarioJson, scenarioErr]);

    const aggregateTiles = useMemo(() => {
        if (!run?.aggregate) return [];
        return Object.entries(run.aggregate).map(([k, v]) => ({
            label: k,
            value: typeof v === "number" ? fmtNum(v) : (v ?? "—").toString(),
        }));
    }, [run]);

    async function handleDelete() {
        setDeleting(true);
        try {
            await runsApi.remove(id);
            navigate("/run/history");
        } catch (e) {
            setErr(serverDetail(e));
            setDeleting(false);
            setConfirmDel(false);
        }
    }

    async function handleCancel() {
        setCancelling(true);
        try {
            await runsApi.cancel(id);
            // Trigger an immediate re-fetch so the badge moves to cancelling.
            await fetchRun();
        } catch (e) {
            setErr(serverDetail(e));
        } finally {
            setCancelling(false);
            setConfirmCancel(false);
        }
    }

    async function downloadFile(name) {
        try {
            const blob = await runsApi.artifactBlob(id, name);
            const a = document.createElement("a");
            const url = URL.createObjectURL(blob);
            a.href = url;
            a.download = name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 4000);
        } catch (e) {
            setErr(serverDetail(e));
        }
    }

    if (loading) return <div className="text-ux-text-3">Loading run…</div>;
    if (err) {
        return (
            <div className="text-[13px] text-ux-text-1 bg-ux-danger-soft border border-ux-danger/40 rounded-md p-2">
                {err}
            </div>
        );
    }
    if (!run) return null;

    const pngs = (run.artifacts || []).filter((a) => a.mime === "image/png");
    const isJsonish = (a) => a.mime === "application/json";
    const isCsv = (a) => a.mime === "text/csv";

    return (
        <div className="space-y-4">
            {/* HEADER CARD */}
            <Card>
                <div className="flex flex-wrap items-center gap-3">
                    <StatusPill status={run.status} testId="run-detail-status" />
                    {(run.dynamics_backend === "jsbsim_hybrid" ||
                        run.backend?.driver === "jsbsim_hybrid") && (
                        <span
                            className="inline-flex items-center px-2 h-6 rounded-full text-[11px] font-medium bg-ux-accent-soft text-ux-accent border border-ux-accent/30"
                            data-testid="run-detail-jsbsim-pill"
                            title="This run executed on the JSBSim Hybrid 6-DoF driver"
                        >
                            JSBSim Hybrid
                        </span>
                    )}
                    {Array.isArray(run.backend?.vehicles) && run.backend.vehicles.length > 0 && (
                        <span
                            className="inline-flex items-center gap-2 px-2 h-6 rounded-full text-[10px] font-mono bg-ux-bg-2 text-ux-text-2 border border-ux-border-1"
                            data-testid="run-detail-fcs-aero-pill"
                            title="Per-vehicle FCS template + aero source"
                        >
                            {run.backend.vehicles.map((v, i) => (
                                <span key={i}>
                                    {v.role}: {v.fcs_template || "no-fcs"} / {v.aero_source}
                                    {i < run.backend.vehicles.length - 1 ? " · " : ""}
                                </span>
                            ))}
                        </span>
                    )}
                    <h1
                        className="text-[18px] font-semibold text-ux-text-1 truncate font-mono"
                        data-testid="run-detail-name"
                    >
                        {run.name}
                    </h1>
                    <span className="text-[11px] text-ux-text-3 font-mono">{run.id}</span>
                    <CopyButton text={run.id} label="Copy id" testId="run-detail-copy-id" />
                    <span className="text-[12px] text-ux-text-2 font-mono">{run.run_type}</span>
                    <span className="text-[12px] text-ux-text-2">{fmtDuration(run.duration_s)}</span>
                    <span className="text-[12px] text-ux-text-3">
                        submitted {relativeTime(run.submitted_at)} · started {relativeTime(run.started_at)} · finished{" "}
                        {relativeTime(run.finished_at)}
                    </span>
                    <div className="flex-1" />
                    <SpinnerButton variant="secondary" onClick={fetchRun} testId="run-detail-refresh">
                        <RefreshCw className="w-4 h-4" /> Refresh
                    </SpinnerButton>
                    {cancellable && (
                        <SpinnerButton
                            variant="warning"
                            loading={cancelling || run?.status === "cancelling"}
                            disabled={!cancellable || cancelling || run?.status === "cancelling"}
                            onClick={() => setConfirmCancel(true)}
                            testId="run-detail-cancel-btn"
                        >
                            <X className="w-4 h-4" />{" "}
                            {run?.status === "cancelling" ? "Cancelling…" : "Cancel run"}
                        </SpinnerButton>
                    )}
                    <SpinnerButton
                        variant="danger"
                        disabled={inflight}
                        onClick={() => setConfirmDel(true)}
                        testId="run-detail-delete"
                    >
                        <Trash2 className="w-4 h-4" /> Delete
                    </SpinnerButton>
                </div>
            </Card>

            {/* Phase 13 (rescoped) — yellow caveat banner for JSBSim sweep / GC tech preview. */}
            {run.caveat && (
                <div
                    className="text-[13px] leading-snug rounded-md border px-3 py-2 bg-ux-warning-soft border-ux-warning/40 text-ux-text-1"
                    data-testid="run-detail-caveat-banner"
                >
                    <span className="font-semibold mr-1">Tech preview:</span>
                    {run.caveat}
                </div>
            )}

            {/* Phase 4 — credibility badge + report export + reviewer flow.
                Renders only for succeeded runs (the only state where the
                report endpoint produces a meaningful artefact and the
                review state machine accepts transitions). */}
            {run.status === "succeeded" && (
                <Card title="Credibility · Report · Review" testId="run-detail-phase4-card">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                        <ConfidenceBadge
                            confidence={(run.provenance || {}).confidence}
                        />
                        <ExportReportButton runId={run.id} />
                        <span
                            className="text-[11px] text-ux-text-3 font-mono"
                            data-testid="run-detail-output-hash"
                        >
                            output_hash: {(run.provenance || {}).output_hash || "—"}
                        </span>
                    </div>
                    <ReviewControls run={run} onUpdated={() => fetchRun()} />
                </Card>
            )}

            {/* Module 8 — parameter-provenance snapshot (immutable). */}
            {run.status !== "queued" && run.status !== "running" && (
                <ParameterProvenanceSection runId={run.id} />
            )}

            <SegmentedTabs
                tabs={TABS.map((t) => {
                    if (t !== "Trajectory") return t;
                    const hasTraj = !!(run.artifacts || []).find((a) => a.name === "trajectories.csv");
                    const eligible = run.status === "succeeded" && hasTraj;
                    return {
                        label: t,
                        disabled: !eligible,
                        title: eligible
                            ? undefined
                            : "Trajectory only available for succeeded runs that wrote trajectories.csv.",
                    };
                })}
                value={tab}
                onChange={setTab}
                testIdPrefix="run-detail-tab"
            />

            {/* SUMMARY */}
            {tab === "Summary" && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="run-detail-summary">
                    <div className="lg:col-span-2 space-y-4">
                        {run.error && (
                            <Card title="Error">
                                <div className="text-[13px] text-ux-danger font-medium mb-2">
                                    {run.error.type}: {run.error.message}
                                </div>
                                {run.error.traceback && (
                                    <YamlViewer
                                        value={run.error.traceback}
                                        testId="run-detail-traceback"
                                        maxHeight="40vh"
                                    />
                                )}
                            </Card>
                        )}
                        {run.run_type === "monte_carlo" && aggregateTiles.length > 0 && (
                            <Card title="Aggregate">
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                    {aggregateTiles.map((t) => (
                                        <KpiTile
                                            key={t.label}
                                            label={t.label.replace(/_/g, " ")}
                                            value={t.value}
                                            testId={`run-detail-tile-${t.label}`}
                                        />
                                    ))}
                                </div>
                            </Card>
                        )}
                        {run.run_type === "sweep" && (
                            <Card title="Sweep">
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    <KpiTile label="rows" value={run.sweep_rows ?? "—"} testId="run-detail-tile-sweep-rows" />
                                    <KpiTile label="duration" value={fmtDuration(run.duration_s)} testId="run-detail-tile-sweep-duration" />
                                    <KpiTile
                                        label="artefact count"
                                        value={run.artifacts?.length ?? 0}
                                        testId="run-detail-tile-sweep-artefact-count"
                                    />
                                </div>
                            </Card>
                        )}
                        {run.run_type === "guidance_compare" && (
                            <Card title="Guidance Comparison">
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    <KpiTile label="rows" value={run.guidance_rows ?? "—"} />
                                    <KpiTile label="duration" value={fmtDuration(run.duration_s)} />
                                    <KpiTile label="artefact count" value={run.artifacts?.length ?? 0} />
                                </div>
                            </Card>
                        )}
                        {scenarioJson && (
                            <Card title="scenario.json">
                                <JsonViewer value={scenarioJson} testId="run-detail-scenario-json" />
                            </Card>
                        )}
                        {scenarioErr && (
                            <div className="text-[12px] text-ux-warning">scenario.json: {scenarioErr}</div>
                        )}
                    </div>
                    <div className="lg:col-span-1 space-y-4">
                        <LinkUptimeCard runId={run.id} />
                        <Card title="Scenario">
                            <div className="space-y-2 text-[13px]">
                                <KV k="name" v={run.scenario_summary?.name || "—"} testId="run-detail-tile-scenario-name" />
                                <KV k="MC samples" v={run.scenario_summary?.monte_carlo_samples ?? "—"} />
                                <KV k="sweep enabled" v={String(!!run.scenario_summary?.sweep_enabled)} />
                                {run.scenario_summary?.preset_chain?.length > 0 && (
                                    <div className="pt-1 border-t border-ux-border-1">
                                        <div className="label-mini mb-1">preset chain</div>
                                        <div className="font-mono text-[11px] text-ux-text-1 leading-relaxed">
                                            {run.scenario_summary.preset_chain.join(" → ")}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Card>
                        <Card title="Backend">
                            <div className="space-y-2 text-[13px]">
                                <KV k="primary" v={run.backend?.primary || "—"} />
                                <KV
                                    k="jsbsim"
                                    v={
                                        run.backend?.jsbsim_detected
                                            ? `detected · ${run.backend.jsbsim_version || "?"}`
                                            : "not detected"
                                    }
                                />
                                <KV k="source" v={run.source?.mode || "—"} />
                            </div>
                        </Card>
                    </div>
                </div>
            )}

            {/* TRAJECTORY */}
            {tab === "Trajectory" && (
                <div data-testid="run-detail-trajectory">
                    <Trajectory3D
                        runId={run.id}
                        status={run.status}
                        hasTrajectoryArtifact={!!(run.artifacts || []).find(
                            (a) => a.name === "trajectories.csv",
                        )}
                    />
                </div>
            )}

            {/* ARTIFACTS */}
            {tab === "Artifacts" && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="run-detail-artifacts">
                    <div className="lg:col-span-2 space-y-4">
                        <Card title="All artefacts">
                            <div className="overflow-x-auto -m-1">
                                <table className="min-w-full text-[13px]">
                                    <thead className="text-ux-text-3">
                                        <tr className="border-b border-ux-border-2">
                                            <th className="text-left px-3 py-2 font-medium uppercase text-[10px] tracking-wider">Name</th>
                                            <th className="text-right px-3 py-2 font-medium uppercase text-[10px] tracking-wider">Size</th>
                                            <th className="text-left px-3 py-2 font-medium uppercase text-[10px] tracking-wider">MIME</th>
                                            <th className="text-right px-3 py-2 font-medium uppercase text-[10px] tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(run.artifacts || []).map((a) => (
                                            <tr key={a.name} className="border-t border-ux-border-1 align-top">
                                                <td className="px-3 py-2 font-mono text-[12px]">{a.name}</td>
                                                <td className="px-3 py-2 text-right tabular-nums font-mono text-[12px]">{fmtBytes(a.size_bytes)}</td>
                                                <td className="px-3 py-2 text-[12px] text-ux-text-3">{a.mime}</td>
                                                <td className="px-3 py-2 text-right">
                                                    <div className="flex justify-end gap-2 items-start">
                                                        {isCsv(a) && <CsvPreview runId={id} name={a.name} />}
                                                        {isJsonish(a) && <JsonInline runId={id} name={a.name} />}
                                                        <button
                                                            type="button"
                                                            onClick={() => downloadFile(a.name)}
                                                            className="inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded-md text-ux-text-2 hover:text-ux-text-1 hover:bg-ux-bg-2"
                                                            data-testid={`run-detail-download-${a.name}`}
                                                        >
                                                            <Download className="w-3.5 h-3.5" /> Download
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {(run.artifacts || []).length === 0 && (
                                            <tr>
                                                <td colSpan={4} className="px-0 py-0">
                                                    <EmptyState
                                                        testId="run-detail-artifacts-empty"
                                                        title="No artefacts yet"
                                                        subtitle="Artefacts will appear here once the run completes."
                                                        className="rounded-none border-0"
                                                    />
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </div>
                    <div className="lg:col-span-1">
                        <Card title="Plots">
                            {pngs.length === 0 ? (
                                <div className="text-[12px] text-ux-text-3">No PNG artefacts.</div>
                            ) : (
                                <div className="grid grid-cols-2 gap-3">
                                    {pngs.map((p) => (
                                        <ImageThumb key={p.name} runId={id} name={p.name} onOpen={setLightbox} />
                                    ))}
                                </div>
                            )}
                        </Card>
                    </div>
                </div>
            )}

            {/* LOG */}
            {tab === "Log" && (
                <div className="space-y-3" data-testid="run-detail-log">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2 text-[12px] text-ux-text-3">
                            <span>
                                {logData
                                    ? `${logData.total_lines || 0} lines · ${fmtBytes(logData.bytes)}`
                                    : "—"}
                            </span>
                            {logData?.truncated && (
                                <span className="text-ux-warning">truncated</span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <select
                                value={logTail}
                                onChange={(e) => setLogTail(Number(e.target.value))}
                                className="text-[12px] bg-ux-bg-3 border border-ux-border-1 rounded-md px-2 h-8 text-ux-text-1"
                                data-testid="run-detail-log-tail"
                            >
                                {[50, 200, 500, 1000].map((n) => (
                                    <option key={n} value={n}>
                                        tail {n}
                                    </option>
                                ))}
                            </select>
                            <SpinnerButton
                                variant="secondary"
                                loading={logLoading}
                                onClick={fetchLog}
                                testId="run-detail-log-refresh"
                            >
                                <RefreshCw className="w-4 h-4" /> Refresh
                            </SpinnerButton>
                            <SpinnerButton
                                variant={logFollow ? "primary" : "secondary"}
                                onClick={() => setLogFollow(!logFollow)}
                                testId="run-detail-log-follow"
                            >
                                {logFollow ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                                {logFollow ? "Following" : "Follow"}
                            </SpinnerButton>
                        </div>
                    </div>
                    {logErr && (
                        <div className="text-[13px] text-ux-text-1 bg-ux-danger-soft border border-ux-danger/40 rounded-md p-2">
                            {logErr}
                        </div>
                    )}
                    <YamlViewer value={logData?.log || ""} testId="run-detail-log-content" maxHeight="70vh" />
                </div>
            )}

            <ConfirmDialog
                open={confirmDel}
                title="Delete run?"
                message={`Delete "${run.name}" and all its artefacts? This cannot be undone.`}
                confirmLabel="Delete"
                loading={deleting}
                onCancel={() => setConfirmDel(false)}
                onConfirm={handleDelete}
            />

            <ConfirmDialog
                open={confirmCancel}
                title={`Cancel run "${run.name}"?`}
                message="The worker will exit at the next sample boundary. Any partial artefacts already written will be kept."
                confirmLabel="Cancel run"
                cancelLabel="Keep running"
                loading={cancelling}
                onCancel={() => setConfirmCancel(false)}
                onConfirm={handleCancel}
                confirmTestId="run-detail-cancel-confirm"
            />

            {lightbox && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm"
                    onClick={() => setLightbox(null)}
                    data-testid="run-detail-lightbox"
                >
                    <button
                        type="button"
                        className="absolute top-4 right-4 text-ux-text-2 hover:text-ux-text-1"
                        onClick={() => setLightbox(null)}
                        data-testid="run-detail-lightbox-close"
                        aria-label="Close"
                    >
                        <X className="w-6 h-6" />
                    </button>
                    <img src={lightbox.src} alt={lightbox.name} className="max-h-[90vh] max-w-[90vw] object-contain" />
                </div>
            )}
        </div>
    );
}

function KV({ k, v, testId }) {
    return (
        <div className="flex items-baseline justify-between gap-3 py-1" data-testid={testId}>
            <span className="text-[11px] uppercase tracking-wider text-ux-text-3">{k}</span>
            <span className="font-mono text-[12px] text-ux-text-1 break-all text-right">
                {String(v ?? "—")}
            </span>
        </div>
    );
}
